/**
 * Connection messages drawn over the game (port addition; the SWF has no asset for them, so they are built at
 * runtime like the language switch): one line at the bottom of the stage with, by priority,
 *   1. the retry countdown of a blocking backend call ("No connection to the server. Retrying in 4 s…"), clickable
 *      to retry at once,
 *   2. the lost-connection indicator while the transport reconnects,
 *   3. a short notice that disappears by itself ("Connection restored", "Profile data updated").
 * Plus a restart button for the error splash of failures the game cannot recover from.
 * On a wide screen (a phone, a tablet, the app: NetOverlayView) the line is at the bottom of the shown safe area, as
 * large as the battle's enlarged log (its main line 14 CSS px and more); in a battle while the touch controls are shown
 * (the local turn: their hint is at the bottom) under the team bars at the top, where it does not cover the current
 * worm (else at the bottom above the controls).
 */
import { MouseEvent, Sprite, TextField, TextFormat } from '@flash';

const STAGE_W = 807;
const STAGE_H = 600;
const PAD_X = 12;
const PAD_Y = 6;
/** The main line's size (px as drawn) and what it measures at least on a wide screen (CSS px). */
const LINE_PX = 13;
const LINE_CSS = 14;
/** Room (CSS px) from the shown safe area's sides and above the touch controls' clusters. */
const EDGE_CSS = 8;

/** A rectangle in stage coordinates. */
interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where the overlay is laid out on a wide screen: the shown safe area (stage coordinates), CSS px per stage px, and the
 * battle's touch controls' clusters it stays above (null: none).
 */
export interface NetOverlayView {
  area: Area;
  css: number;
  above: Area[] | null;
  /** The top it goes to instead (under the battle's team bars while the touch controls are shown), or null. */
  top?: number | null;
  /** What it must not cover at the top (the battle's current worm), or null. */
  avoid?: Area | null;
}

function label(text: string, size: number, bold: boolean, color: number): TextField {
  const tf = new TextField();
  tf.defaultTextFormat = new TextFormat('Arial', size, color, bold);
  tf.selectable = false;
  tf.autoSize = 'left';
  tf.text = text;
  return tf;
}

export class NetOverlay {
  readonly root = new Sprite();
  private readonly box = new Sprite();
  private readonly line: TextField;
  private readonly hint: TextField;
  private retry: { text: string; hint: string | null } | null = null;
  private status: string | null = null;
  private notice: { text: string; until: number; raw: boolean } | null = null;
  private drawn = '\n';
  /** The box's size as drawn (scale 1). */
  private boxW = 0;
  private boxH = 0;
  /** The box's scale and place last set. */
  private at = '';
  /** Called when the player clicks the retry line. */
  onRetryClick: (() => void) | null = null;

  constructor() {
    this.root.name = '$net_overlay';
    this.line = label('', LINE_PX, true, 0xffffff);
    this.hint = label('', 11, false, 0xdddddd);
    this.box.addChild(this.line);
    this.box.addChild(this.hint);
    this.box.buttonMode = true;
    this.box.addEventListener(MouseEvent.CLICK, () => {
      if (this.retry && this.onRetryClick) this.onRetryClick();
    });
    this.root.addChild(this.box);
    this.root.visible = false;
  }

  /** Retry countdown of a blocking call (`null` hides it); `hint` is a second, smaller line. */
  setRetry(text: string | null, hint: string | null = null): void {
    this.retry = text === null ? null : { text, hint };
  }

  /** Persistent connection indicator (`null` hides it). */
  setStatus(text: string | null): void {
    this.status = text;
  }

  /**
   * Short message shown until `untilMs` (bridge clock). `raw`: the text is DISPLAY text (already translated, with a
   * player's or a clan's name in it), shown as it is.
   */
  showNotice(text: string, untilMs: number, raw = false): void {
    this.notice = { text, until: untilMs, raw };
  }

  /** The text on screen from the next frame on ('' when hidden). */
  get text(): string {
    return this.retry ? this.retry.text : this.status ?? (this.notice ? this.notice.text : '');
  }

  /**
   * Redraws for the current frame and keeps the overlay on top of `container` (in stage coordinates); `view`: where it
   * goes on a wide screen (asked while something is shown; null: the bottom of the original's window, as drawn).
   */
  update(container: any, nowMs: number, view: (() => NetOverlayView | null) | null = null): void {
    if (this.notice && nowMs >= this.notice.until) this.notice = null;
    const main = this.text;
    const sub = this.retry ? this.retry.hint ?? '' : '';
    const raw = !this.retry && this.status === null && !!this.notice && this.notice.raw;
    const key = main + '\n' + sub + (raw ? '\n' : '');
    if (key !== this.drawn) {
      this.drawn = key;
      if (main) this.draw(main, sub, raw);
    }
    if (!main) {
      if (this.root.parent) this.root.parent.removeChild(this.root);
      this.root.visible = false;
      return;
    }
    this.root.visible = true;
    this.place(view ? view() : null);
    if (container && (this.root.parent !== container || container.getChildIndex(this.root) !== container.numChildren - 1)) container.addChild(this.root);
  }

  /**
   * The box at the bottom of the original's window (no view), or of the shown safe area, enlarged so that its main line
   * measures LINE_CSS CSS px (within the area's width), above the touch controls' clusters: the top of the row at the
   * screen's bottom (the d-pad, the fire button) and of any cluster under it.
   */
  private place(view: NetOverlayView | null): void {
    const w = this.boxW;
    const h = this.boxH;
    let k = 1;
    let x = Math.round((STAGE_W - w) / 2);
    let y = Math.round(STAGE_H - h - 24);
    if (view && w > 0 && view.css > 0) {
      const a = view.area;
      const edge = EDGE_CSS / view.css;
      k = Math.max(1, Math.min(LINE_CSS / (LINE_PX * view.css), (a.width - 2 * edge) / w));
      x = a.x + (a.width - w * k) / 2;
      let bottom = a.y + a.height - Math.max(24, edge);
      const zones = view.above ?? [];
      if (zones.length) {
        bottom = Math.min(bottom, Math.max(...zones.map((z) => z.y)) - edge);
        for (const z of zones) if (z.x < x + w * k && x < z.x + z.width) bottom = Math.min(bottom, z.y - edge);
      }
      y = Math.max(a.y + edge, bottom - h * k);
      // The local turn: at the top, under the team bars, unless the current worm is there.
      const t = view.top;
      const av = view.avoid;
      if (t !== null && t !== undefined) {
        const covers = !!av && x < av.x + av.width && av.x < x + w * k && t < av.y + av.height && av.y < t + h * k;
        if (!covers) y = t;
      }
    }
    const at = `${k} ${x} ${y}`;
    if (at === this.at) return;
    this.at = at;
    this.box.scaleX = this.box.scaleY = k;
    this.box.x = x;
    this.box.y = y;
  }

  private draw(main: string, sub: string, raw = false): void {
    if (raw) {
      // Display text: not run through the translation again (a name in it stays as it is).
      this.line.text = '';
      this.line.replaceText(0, 0, main);
    } else this.line.text = main;
    this.hint.text = sub;
    const w = Math.max(this.line.width, sub ? this.hint.width : 0) + PAD_X * 2;
    const h = this.line.height + (sub ? this.hint.height : 0) + PAD_Y * 2;
    this.line.x = (w - this.line.width) / 2;
    this.line.y = PAD_Y;
    this.hint.x = (w - this.hint.width) / 2;
    this.hint.y = PAD_Y + this.line.height;
    this.hint.visible = !!sub;
    const g = this.box.graphics;
    g.clear();
    g.beginFill(0x000000, 0.78);
    g.drawRoundRect(0, 0, w, h, 10, 10);
    g.endFill();
    this.boxW = w;
    this.boxH = h;
  }
}

/** Adds a "Restart" button under the message of an error splash (`_splash_system_error`, field `mes`). */
export function addRestartButton(splash: any, text: string, onClick: () => void): Sprite {
  const btn = new Sprite();
  btn.name = '$net_restart';
  const tf = label(text, 12, true, 0xffffff);
  const w = tf.width + 28;
  const h = tf.height + 8;
  tf.x = 14;
  tf.y = 4;
  btn.graphics.beginFill(0x511414, 1);
  btn.graphics.drawRoundRect(0, 0, w, h, 8, 8);
  btn.graphics.endFill();
  btn.addChild(tf);
  btn.buttonMode = true;
  btn.addEventListener(MouseEvent.CLICK, () => onClick());
  const mes = splash && splash.mes;
  const cx = mes ? mes.x + mes.width / 2 : STAGE_W / 2;
  const y = mes ? mes.y + mes.height + 12 : STAGE_H / 2 + 40;
  btn.x = Math.round(cx - w / 2);
  btn.y = Math.round(y);
  if (splash && typeof splash.addChild === 'function') splash.addChild(btn);
  return btn;
}
